import { type EnvironmentProviders, type Provider, inject } from '@angular/core';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { PERMISSION_AUTH_TOKEN, type PermissionAuthTokenProvider, providePermissionUI } from '@mtm/permission-ui';

import { environment } from '../../../environments/environment';

/**
 * Wires the `@mtm/permission-ui` bundle components (yalc-linked lib) for the route that
 * renders them. Spread into a lazy route's `providers`, never into `AppModule.providers`:
 * `providePermissionUI()` calls `provideHttpClient(withInterceptorsFromDi())` internally, and
 * in the ROOT injector that registers a second `HTTP_INTERCEPTOR_FNS` entry for the DI
 * interceptor chain — every request in the app would then run `AppHttpInterceptor` and
 * `HttpCancelInterceptor` twice. On a route it gets its own child `HttpClient` instead, whose
 * legacy chain still resolves the root `HTTP_INTERCEPTORS` (they are `multi` and inherited), so
 * permission calls keep the `Authorization: Bearer <app token>` header `AppHttpInterceptor`
 * attaches.
 *
 * `apiBaseUrl` is the IAM app gateway plus `/v1` — the library hits
 * `/v1/tenant/rbac/permissions/...` on it, mirroring `studio-admin-app`'s `app.config.ts`.
 *
 * `iamBaseUrl` is the IAM service origin, used only by `mtm-account-page`'s COMPANY-mode invite
 * (`POST /api/v1/registration`). Every other call goes to `apiBaseUrl`.
 *
 * `PERMISSION_AUTH_TOKEN` is what authenticates that one call, and it has to be wired explicitly
 * because NEITHER interceptor covers it: `AppHttpInterceptor` skips `environment.iam.iamApiUrl`
 * outright (IAM SDK traffic owns its own auth), and `mtmAppTokenInterceptor` is both scoped to
 * `environment.drive.apiUrl` and a FUNCTIONAL interceptor — the `provideHttpClient(
 * withInterceptorsFromDi())` inside `providePermissionUI()` re-provides `HTTP_INTERCEPTOR_FNS`
 * here, shadowing the root's functional interceptors (the legacy `HTTP_INTERCEPTORS` are not
 * re-provided, so they are still inherited, which is what keeps the gateway calls authenticated).
 * It is a `useFactory` rather than `providePermissionUI({ authToken })` because the library takes
 * that option by value and calls it outside any injection context, so `inject()` inside the
 * closure would throw — the factory injects once and the closure just reads the bridge.
 *
 * No `translations` adapter is passed: the library's keys (`permission.*`, `matrix.*`) live in
 * its own bundled en/fr dictionaries, not in this app's `assets/<lang>.json`, so bridging to
 * `TranslateService` would only render raw keys.
 */
export function provideMtmPermissionUI(): (Provider | EnvironmentProviders)[] {
  return [
    ...providePermissionUI({
      apiBaseUrl: `${environment.admin.apiUrl}/v1`,
    }),
    {
      provide: PERMISSION_AUTH_TOKEN,
      useFactory: (): PermissionAuthTokenProvider => {
        const bridge = inject(MtmAuthBridgeService);
        return () => bridge.getAppTokenSync();
      },
    },
  ];
}
